import { useEffect, useState } from 'react'

import { useWindowSize } from '@cox/core-ui8/dist/useWindowSize'
import axios from 'axios'

import { LayoutType, ReactVideoProps } from './types'
import { YOUTUBE_ICON } from '../../assets/images'
import { getSpacing } from '../../utils/spacing-util'
import { getVideoThumbnail, playIcon } from '../../utils/video-util'
import ReactImage from '../ReactImage'
import IsInEditorText from '../shared/IsInEditorText'
import ReactImageOverlay from '../shared/ReactImageOverlay'
import './ReactVideo.scss'

const ReactVideo = (props: ReactVideoProps) => {
  const {
    isInEditor = false,
    id,
    layout,
    accessibilityLabel = '',
    youtubeHeight = '315',
    youtubeWidth = '560',
    youtubeVideoId,
    youtubeAspectRatio = '0.5625',
    autoplay = false,
    youtubeMute,
    videoIcon = YOUTUBE_ICON,
    videoAltTxt = '',
    youtubeLoop = false,
    youtubePlaysInline = false,
    youtubeRel = false,
    left = false,
    right = false,
    top = false,
    bottom = false
  } = props

  // Smart logic: Handle browser autoplay constraints and user intent
  const getSmartMuteValue = () => {
    // If autoplay is enabled, MUST be muted (browser requirement)
    if (autoplay) {
      return true // Override author choice - browser won't allow unmuted autoplay
    }

    // Default to muted unless author explicitly sets false
    return youtubeMute !== false
  }

  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [mute, setMute] = useState(getSmartMuteValue())
  const windowSize = useWindowSize()

  const YT_SRC = `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=${isPlaying ? 1 : 0}&mute=${mute ? 1 : 0}&rel=${
    youtubeRel ? 1 : 0
  }&playsinline=${youtubePlaysInline ? 1 : 0}&loop=${youtubeLoop}&playsinline=${youtubePlaysInline ? 1 : 0}&loop=${
    youtubeLoop ? 1 : 0
  }${youtubeLoop ? `&playlist=${youtubeVideoId}` : ''}`

  let youtubeAspectRatioPercentage
  if (layout === LayoutType.RESPONSIVE && isPlaying) {
    const percentage = (parseFloat(youtubeAspectRatio) * 100).toFixed(2)
    youtubeAspectRatioPercentage = `${percentage}%`
  }

  const handlePlay = () => {
    setIsPlaying(true)

    // For manual play, default to muted unless author explicitly sets false
    setMute(youtubeMute !== false)
  }

  /* As not all YouTube videos have maxres versions of the thumbnail by default, 
  this is to get the highest resolution thumbnail as possible.
  */
  const [thumbnailFilename, setThumbnailFilename] = useState('mqdefault.jpg')
  const getThumbnailFilename = () => {
    axios
      .get(getVideoThumbnail(youtubeVideoId, 'maxresdefault.jpg'), {
        validateStatus: (status) => status >= 200
      })
      .then((response) => {
        if (response.status == 200) {
          setThumbnailFilename('maxresdefault.jpg')
        } else {
          axios
            .get(getVideoThumbnail(youtubeVideoId, 'hqdefault.jpg'), {
              validateStatus: (status) => status >= 200
            })
            .then((response) => {
              if (response.status == 200) {
                setThumbnailFilename('hqdefault.jpg')
              } else {
                axios
                  .get(getVideoThumbnail(youtubeVideoId, 'sddefault.jpg'), {
                    validateStatus: (status) => status >= 200
                  })
                  .then((response) => {
                    if (response.status == 200) {
                      setThumbnailFilename('sddefault.jpg')
                    } else {
                      setThumbnailFilename('mqdefault.jpg')
                    }
                  })
              }
            })
        }
      })
  }

  useEffect(() => {
    getThumbnailFilename()
  }, [youtubeVideoId])

  return (
    <>
      {IsInEditorText(isInEditor, 'React Video')}
      <div
        id={id}
        className={`react-video-container ${getSpacing(top, right, bottom, left)}`}
        onClick={() => {
          if (!isPlaying) handlePlay()
        }}
      >
        {layout === LayoutType.FIXED && (
          <div className='yt-fixed-video' style={{ display: 'flex' }}>
            {!isPlaying && (
              <a href={'#'} role={'button'} onClick={(e) => e.preventDefault()}>
                <ReactImageOverlay
                  overlay={playIcon(windowSize.width, windowSize.height, videoIcon)}
                  onClick={handlePlay}
                  alt={accessibilityLabel}
                  ariaLabel={videoAltTxt}
                >
                  <ReactImage
                    src={getVideoThumbnail(youtubeVideoId, thumbnailFilename)}
                    alt={videoAltTxt}
                    imageHeight={Number(youtubeHeight)}
                    imageWidth={Number(youtubeWidth)}
                    ariaLabel={accessibilityLabel}
                  />
                </ReactImageOverlay>
              </a>
            )}
            {isPlaying && (
              <iframe
                width={youtubeWidth}
                height={youtubeHeight}
                src={YT_SRC}
                aria-label={accessibilityLabel}
                allow='autoplay'
                title={id}
              />
            )}
          </div>
        )}
        {layout === LayoutType.RESPONSIVE && (
          <div className='yt-responsive-video' style={{ position: 'relative', paddingBottom: youtubeAspectRatioPercentage }}>
            {!isPlaying && (
              <a href={'#'} role={'button'} onClick={(e) => e.preventDefault()}>
                <ReactImageOverlay
                  overlay={playIcon(windowSize.width, windowSize.height, videoIcon)}
                  onClick={handlePlay}
                  alt={accessibilityLabel}
                  ariaLabel={videoAltTxt}
                >
                  <ReactImage
                    src={getVideoThumbnail(youtubeVideoId, thumbnailFilename)}
                    alt={videoAltTxt}
                    imageHeight={100}
                    imageWidth={100}
                    imageUnit='%'
                    ariaLabel={accessibilityLabel}
                  />
                </ReactImageOverlay>
              </a>
            )}
            {isPlaying && (
              <iframe
                style={{ position: 'absolute', top: '0', left: '0' }}
                width='100%'
                height='100%'
                src={YT_SRC}
                allowFullScreen={true}
                allow='autoplay'
                title={id}
                aria-label={accessibilityLabel}
              />
            )}
          </div>
        )}
      </div>
    </>
  )
}
export default ReactVideo
